import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ConnectBalances } from '@stripe/react-connect-js';
import { PageHeader } from '../../../components/ui';
import connectApi from '../../../services/connectApi';
import PayoutActivityTable from '../../connect/finance/payout-activity-table';
import FirstPayoutBanner from '../../connect/finance/first-payout-banner';
import FinButton from '../../connect/finance/fin-button';
import { FinTableSkeleton } from '../../connect/finance/fin-skeleton';
import { upcomingPayouts } from '../../connect/finance/upcoming-payout';
import COPY from '../../connect/finance/connect-finance-copy';
import useLegacyFinance from '../../connect/finance/use-legacy-finance';
import { useFinanceSection } from '../section/finance-section-context';
import PayoutsFilterBar, {
  DEFAULT_FILTERS, applyFilters, emptyCopyFor, hasClientFilter, resolveDateWindow,
} from './payouts-filter-bar';
import { csvFilename, payoutsCsv } from './payouts-export';
import { downloadCsv } from '../csv';
import FinancePageShell from './finance-page-shell';

const PAGE_SIZE = 8;

// Stripe's <ConnectBalances/> owns Pay out and tells this page nothing: its
// only callbacks are about loading (react-connect-js 3.4.4, the latest; only
// Overview's instant payouts card reports a payout). A payout made there left
// the table below as it was until a reload (owner, 2026-10-09). So the page
// notices the click itself, focus leaving this window for Stripe's frame, and
// re-reads the balance and the list at these times after it, until a payout
// the list did not have shows up. The restaurant spends ten seconds to a
// minute in Stripe's dialog before the payout exists, and the webhook writes
// our row a few seconds after that.
const STRIPE_CLICK_FOLLOW_MS = [3000, 5000, 8000, 12000, 20000, 30000, 45000, 60000];
// Focus coming back to the page after such a click (the dialog is closed):
// read at once, then for the webhook's few seconds. This ends the follow.
const FOCUS_RETURN_FOLLOW_MS = [0, 2000, 5000, 10000];

// Our own payouts list (spec 2026-09-22 §5.1, frame 2b), not Stripe's iframe:
// the same columns as Overview plus Arrive by, filters and export that are
// ours, and a row that opens our own payout detail page by Stripe's id.
//
// Round 1 kept Stripe's full payouts component here, inside a collapsed
// "Payout settings" card. Round 2 split the hero's actions into modals; the
// rail correction (2026-09-22) removes the hero from this page entirely —
// Overview is the one page with the hero and an instant-payout card now —
// and puts Stripe's own `<ConnectBalances/>` in its place, full width, in
// the same card frame this page already uses everywhere else. It alone owns
// Pay out / See details, instant vs standard, and eligibility — and, since
// 2026-10-20 (owner: restaurants edit their payout schedule again), its own
// "Update" beside the next payout, Stripe's schedule editor, granted by
// edit_payout_schedule on the finance Account Session. Overview offers our
// own modal beside its Schedule row, and the Manager app a sheet; all three
// write the same Stripe setting and each reads it fresh.
//
// The legacy footnote stays for a company that was paid before Stripe: those
// statements are its own records, on their own page.
const Payouts = () => {
  const { companyId, status, instance, sessionFailure, retrySession } = useFinanceSection();
  const navigate = useNavigate();
  const hasLegacyHistory = useLegacyFinance(companyId);
  const copy = COPY.restaurant;
  const ready = !!status?.hasAccount && status?.connectStatus === 'READY';

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [balance, setBalance] = useState({ data: null, error: '', loading: true });
  const [list, setList] = useState({ data: [], error: '', loading: true });
  const requestId = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const window_ = useMemo(() => resolveDateWindow(filters.date), [filters.date]);

  const loadBalance = useCallback(async () => {
    if (!companyId) return;
    requestId.current += 1;
    const id = requestId.current;
    setBalance((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const data = connectApi.toConnectBalance(await connectApi.getConnectBalance(companyId));
      if (!mounted.current || id !== requestId.current) return;
      if (!data) throw new Error('balance');
      setBalance({ data, error: '', loading: false });
    } catch {
      if (!mounted.current || id !== requestId.current) return;
      setBalance((prev) => ({ ...prev, error: copy.sections.balance.error, loading: false }));
    }
  }, [companyId, copy]);

  // Its own counter: the balance and the list refresh independently, and a
  // Date change must not let an older list write over a newer one.
  const listRequest = useRef(0);
  const loadList = useCallback(async () => {
    if (!companyId) return;
    listRequest.current += 1;
    const id = listRequest.current;
    setList((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const rows = connectApi.toFinancePayouts(
        await connectApi.getFinancePayouts(companyId, window_.from, window_.to),
      );
      if (!mounted.current || id !== listRequest.current) return;
      setList({ data: rows, error: '', loading: false });
    } catch {
      if (!mounted.current || id !== listRequest.current) return;
      // Never an empty list over a refused read.
      setList({ data: [], error: copy.sections.payoutActivity.error, loading: false });
    }
  }, [companyId, copy, window_.from, window_.to]);

  useEffect(() => {
    if (ready) loadBalance();
  }, [ready, loadBalance]);

  useEffect(() => {
    if (!ready) return;
    setPage(1);
    loadList();
  }, [ready, loadList]);

  // The follow after a click in Stripe's frame (STRIPE_CLICK_FOLLOW_MS).
  // Quiet on purpose, like Overview's instant payout follow: no skeleton over
  // the rows on screen, and a refused read leaves them standing. Each read
  // checks the same counters the loud reads bump, so a Date change or a retry
  // mid-follow wins over it.
  const balancesBox = useRef(null);
  const followTimers = useRef([]);
  const following = useRef(false);
  const quietSeq = useRef(0);
  // The payouts the follow started from, and the loud read (listRequest) they
  // came from: a baseline only counts for the window it was read in.
  const knownPayouts = useRef(null);
  const baselineFor = useRef(0);
  const listNow = useRef(list);
  listNow.current = list;

  const stopFollowing = useCallback(() => {
    followTimers.current.forEach(clearTimeout);
    followTimers.current = [];
  }, []);

  const quietRead = useCallback(async () => {
    if (!companyId) return;
    quietSeq.current += 1;
    const seq = quietSeq.current;
    const listId = listRequest.current;
    const balanceId = requestId.current;
    const [rows, data] = await Promise.all([
      connectApi.getFinancePayouts(companyId, window_.from, window_.to)
        .then((res) => connectApi.toFinancePayouts(res))
        .catch(() => null),
      connectApi.getConnectBalance(companyId)
        .then((res) => connectApi.toConnectBalance(res))
        .catch(() => null),
    ]);
    // Only the newest quiet read writes: the timers do not wait for each
    // other, and an older answer landing late would put back the rows from
    // before the payout after the follow had already stopped.
    if (!mounted.current || seq !== quietSeq.current) return;
    if (data && balanceId === requestId.current) setBalance({ data, error: '', loading: false });
    if (!rows || listId !== listRequest.current) return;
    setList({ data: rows, error: '', loading: false });
    const ids = rows.map((row) => row.stripePayoutId).filter(Boolean);
    // No baseline for this window (the list had failed or not landed at the
    // click, or the Date window changed since): this read becomes it and
    // ends nothing, so payouts that were already there never stop the follow.
    if (!knownPayouts.current || baselineFor.current !== listId) {
      knownPayouts.current = new Set(ids);
      baselineFor.current = listId;
      return;
    }
    if (ids.some((id) => !knownPayouts.current.has(id))) {
      following.current = false;
      stopFollowing();
    }
  }, [companyId, window_.from, window_.to, stopFollowing]);
  // Timers call the newest read, so a Date change mid-follow asks for the
  // window on screen.
  const quietReadRef = useRef(quietRead);
  quietReadRef.current = quietRead;

  const follow = useCallback((waits, last) => {
    stopFollowing();
    followTimers.current = waits.map((wait, i) => setTimeout(() => {
      if (last && i === waits.length - 1) following.current = false;
      quietReadRef.current();
    }, wait));
  }, [stopFollowing]);

  useEffect(() => {
    if (!ready) return undefined;
    const onBlur = () => {
      // Focus lands in Stripe's iframe just after this window's blur. A page
      // gone by then has no box, so a pending check starts nothing.
      setTimeout(() => {
        const box = balancesBox.current;
        if (!box || !box.contains(document.activeElement)) return;
        if (!following.current) {
          following.current = true;
          const shown = listNow.current;
          knownPayouts.current = shown.loading || shown.error
            ? null
            : new Set((shown.data || []).map((row) => row.stripePayoutId).filter(Boolean));
          baselineFor.current = listRequest.current;
        }
        follow(STRIPE_CLICK_FOLLOW_MS, false);
      }, 0);
    };
    const onFocus = () => {
      if (following.current) follow(FOCUS_RETURN_FOLLOW_MS, true);
    };
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
      following.current = false;
      stopFollowing();
    };
  }, [ready, companyId, follow, stopFollowing]);

  // The Upcoming rows (spec 2026-09-22 §F): never enter `filtered`, so the
  // filter bar, the "Showing" counts and the CSV export never see them.
  // Hidden under a Status or Method filter (spec 2026-09-22 §13): they have
  // neither, so a reader who narrowed to Failed or Instant is not shown
  // money that is not a payout yet. The Date window leaves them alone.
  const upcoming = useMemo(
    () => (hasClientFilter(filters) ? [] : upcomingPayouts(balance.data)),
    [balance.data, filters],
  );
  // The list lands before the balance (~0.6s against ~1.4s on staging,
  // 2026-09-24), so the table drew its payouts and then grew Upcoming rows
  // on top of them. It waits for both — on the first read only: a balance
  // already on file, a refused balance read (the list then shows without
  // Upcoming), or a filter that hides Upcoming anyway ends the wait.
  const awaitingUpcoming =
    balance.loading && !balance.data && !balance.error && !hasClientFilter(filters);
  const tableLoading = list.loading || awaitingUpcoming;
  const filtered = useMemo(() => applyFilters(list.data || [], filters), [list.data, filters]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const shown = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const changeFilters = (next) => {
    setFilters(next);
    setPage(1);
  };

  const exportCsv = () => downloadCsv(payoutsCsv(filtered), csvFilename(window_));

  return (
    <FinancePageShell>
      <PageHeader
        title="Payouts"
        description="Your balance, when it gets paid out, and everything paid so far."
      />

      {ready ? (
        <>
          <FirstPayoutBanner />
          <section className="overflow-hidden rounded-xl border border-fin-border bg-fin-surface">
            {/* The floor is Stripe's loaded height — 20 + 138 + 20, measured
                on fe-test — so loading does not jump and nothing sits empty
                under the balances (owner, 2026-09-26: 220 left ~42px). More
                content from Stripe still grows the card. */}
            <div ref={balancesBox} data-testid="stripe-balances" className="min-h-[178px] p-5">
              {sessionFailure ? (
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-[13.5px] text-fin-red">{copy.sessionFailed}</p>
                  <FinButton onClick={retrySession}>Retry</FinButton>
                </div>
              ) : !instance ? (
                <FinTableSkeleton rows={4} />
              ) : (
                <ConnectBalances />
              )}
            </div>
          </section>

          <section className="overflow-hidden rounded-xl border border-fin-border bg-fin-surface">
            <PayoutsFilterBar filters={filters} onChange={changeFilters} onExport={exportCsv} />
            <PayoutActivityTable
              variant="full"
              rows={awaitingUpcoming ? [] : shown}
              upcoming={upcoming}
              loading={tableLoading}
              error={list.error}
              onRetry={loadList}
              empty={hasClientFilter(filters) ? 'No payouts match these filters.' : emptyCopyFor(filters.date)}
              // Every row with a Stripe id opens; the session no longer gates
              // it, because the page it opens is ours (spec §6).
              onOpen={(stripePayoutId) => navigate(`/dashboard/finance/payouts/${stripePayoutId}`)}
            />
            {filtered.length > 0 ? (
              <div className="flex items-center justify-between gap-3 border-t border-fin-border px-5 py-3">
                <p className="text-[13px] text-fin-muted">{`Showing ${shown.length} of ${filtered.length} payouts`}</p>
                <div className="flex gap-2">
                  <FinButton size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                    Previous
                  </FinButton>
                  <FinButton size="sm" disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>
                    Next
                  </FinButton>
                </div>
              </div>
            ) : null}
          </section>

          {hasLegacyHistory ? (
            <p className="px-1 text-[13px] text-fin-muted">
              Looking for older payouts?{' '}
              <Link to="/dashboard/finance/legacy" className="font-semibold text-fin-blue hover:text-fin-blue-hover">
                View payout history
              </Link>
            </p>
          ) : null}
        </>
      ) : (
        <p className="text-sm text-fin-ink-2">
          {copy.accountNotReady.before}
          <Link to="/dashboard/finance/business-details" className="font-medium text-fin-blue hover:underline">
            {copy.accountNotReady.linkLabel}
          </Link>
          {copy.accountNotReady.after}
        </p>
      )}
    </FinancePageShell>
  );
};

export default Payouts;
